import { BREAKPOINTS } from 'config/style/breakpoints';
import { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { SegmentTrackingHookReturn } from 'core/utils/segment/useSegmentTracking';
import type { TFunction } from 'i18next';
import { getRegionalOverviewMapPadding } from '@features/regional-overview/regional-overview.constants';
import { useDigitalTrapsStore } from '../../digital-traps.zustand';
import { TRAP_DETAILS_DRAWER_RIGHT_EDGE, type TrapMarker } from '../../domain';
import type { Cluster } from './geo.utils';
import { createRiskMapClusterMarker, type RiskMapClusterColors } from '@features/regional-overview/components/risk-map-cluster';
import { createTrapMarkerHTML, getRiskColor, toRiskMapClusterCounts } from './map-style.utils';
import { createClusterPopupContent, createTrapPopupContent, getPopupClassName } from './popup.utils';

const DIGITAL_TRAPS_CLUSTER_COLORS: RiskMapClusterColors = {
  high: getRiskColor('high'),
  medium: getRiskColor('medium'),
  low: getRiskColor('low'),
  noRisk: getRiskColor('not_applicable'),
  notApplicable: getRiskColor('not_applicable')
};

let currentPopup: mapboxgl.Popup | null = null;
const mapInteractionCleanups = new WeakMap<mapboxgl.Map, () => void>();

const INTERACTION_EVENTS = ['movestart', 'zoomstart', 'dragstart', 'pitchstart', 'rotatestart'];

const closeCurrentTrapPopup = (): void => {
  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
  }
};

const setupMapInteractionListeners = (map: mapboxgl.Map) => {
  if (mapInteractionCleanups.has(map)) return;

  INTERACTION_EVENTS.forEach(event => {
    map.on(event, closeCurrentTrapPopup);
  });

  mapInteractionCleanups.set(map, () => {
    INTERACTION_EVENTS.forEach(event => {
      map.off(event, closeCurrentTrapPopup);
    });
  });
};

export const removeMapInteractionListeners = (map: mapboxgl.Map): void => {
  const cleanup = mapInteractionCleanups.get(map);
  if (!cleanup) return;

  cleanup();
  mapInteractionCleanups.delete(map);
};

export const addHoverEvents = (element: HTMLElement, popup: mapboxgl.Popup, coordinates: [number, number], map: mapboxgl.Map): void => {
  setupMapInteractionListeners(map);

  const openPopup = () => {
    if (currentPopup && currentPopup !== popup) {
      currentPopup.remove();
    }

    popup.setLngLat(coordinates).addTo(map);
    currentPopup = popup;
  };

  const closePopup = () => {
    popup.remove();
    if (currentPopup === popup) {
      currentPopup = null;
    }
  };

  element.addEventListener('mouseenter', openPopup);
  element.addEventListener('mouseleave', closePopup);

  popup.on('close', () => {
    if (currentPopup === popup) {
      currentPopup = null;
    }
  });
};

export const renderMarkers = (
  map: mapboxgl.Map,
  trapsOrClusters: TrapMarker[] | Cluster[],
  isCluster: boolean,
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  segmentTracking: SegmentTrackingHookReturn,
  t: TFunction
): void => {
  clearMarkers(markersRef);

  if (isCluster) {
    renderClusters(map, trapsOrClusters as Cluster[], markersRef, segmentTracking, t);
  } else {
    renderTraps(map, trapsOrClusters as TrapMarker[], markersRef, segmentTracking, t);
  }
};

export const createTrapMarker = (
  map: mapboxgl.Map,
  trap: TrapMarker,
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  segmentTracking: SegmentTrackingHookReturn,
  t: TFunction
): void => {
  const color = getRiskColor(trap.riskLevel);
  const el = createTrapMarkerHTML(trap.count, color);
  const { setSelectedTrapId } = useDigitalTrapsStore.getState();

  const popup = new mapboxgl.Popup({
    offset: 40,
    closeButton: false,
    className: getPopupClassName(trap.riskLevel)
  })
    .on('open', () => {
      segmentTracking.track('Digital Traps - Map Trap Popup Opened', {
        trapId: trap.id,
        trapName: trap.name,
        trapRiskLevel: trap.riskLevel,
        trapCount: trap.count
      });
    })
    .setHTML(createTrapPopupContent(trap, t));

  const coordinates: [number, number] = [trap.longitude, trap.latitude];
  const marker = new mapboxgl.Marker({ element: el }).setLngLat(coordinates).addTo(map);

  el.addEventListener('click', () => {
    setSelectedTrapId(trap.id);
  });

  addHoverEvents(el, popup, coordinates, map);

  markersRef.current.push(marker);
};

export const renderClusters = (
  map: mapboxgl.Map,
  clusters: Cluster[],
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  segmentTracking: SegmentTrackingHookReturn,
  t: TFunction
): void => {
  clusters.forEach(cluster => {
    if (cluster.markers.length > 1) {
      const { marker, element } = createRiskMapClusterMarker(map, cluster.center, toRiskMapClusterCounts(cluster.riskCounts), {
        colors: DIGITAL_TRAPS_CLUSTER_COLORS
      });

      const popup = new mapboxgl.Popup({ offset: 40, closeButton: false, className: 'cluster-popup' })
        .on('open', () => {
          segmentTracking.track('Digital Traps - Map Cluster Popup Opened');
        })
        .setHTML(createClusterPopupContent(cluster, t));

      addHoverEvents(element, popup, cluster.center, map);

      markersRef.current.push(marker);
    } else if (cluster.markers.length === 1) {
      const trap = cluster.markers[0];
      createTrapMarker(map, trap, markersRef, segmentTracking, t);
    }
  });
};

const renderTraps = (
  map: mapboxgl.Map,
  traps: TrapMarker[],
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  segmentTracking: SegmentTrackingHookReturn,
  t: TFunction
): void => {
  traps.forEach(trap => {
    createTrapMarker(map, trap, markersRef, segmentTracking, t);
  });
};

export const clearMarkers = (markersRef: React.MutableRefObject<mapboxgl.Marker[]>): void => {
  if (markersRef.current.length) {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  }

  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
  }
};

export const resetGlobalState = (): void => {
  currentPopup = null;
};

export const recenterMap = (map: mapboxgl.Map, traps: TrapMarker[]): void => {
  if (!map || !traps.length) return;

  const bounds = new mapboxgl.LngLatBounds();

  traps.forEach(trap => {
    bounds.extend([trap.longitude, trap.latitude]);
  });

  map.fitBounds(bounds, {
    padding: getRegionalOverviewMapPadding(),
    maxZoom: 13
  });
};

const SELECTED_TRAP_ZOOM = 14;

export const flyToTrap = (map: mapboxgl.Map, longitude: number, latitude: number): void => {
  if (!map) return;

  const isMobile = window.innerWidth <= BREAKPOINTS.MOBILE;

  map.flyTo({
    center: [longitude, latitude],
    zoom: SELECTED_TRAP_ZOOM,
    // Centre the trap in the strip left visible beside the open details drawer.
    offset: isMobile ? [0, 0] : [TRAP_DETAILS_DRAWER_RIGHT_EDGE / 2, 0]
  });
};
